import { useEffect, useRef, useState } from 'react';
import { getApiRoot } from '../../lib/env';
import { getAuthBearerHeaders } from '../../services/api';
import type { ForecastDaySummary, ForecastHourSummary } from './WeatherDashboard';

export interface ForecastLocationContext {
  cell_id?: number;
  district_code?: string | null;
  service_area_code?: string | null;
  district_name?: string | null;
  region_name?: string | null;
  country_name?: string | null;
  postcode?: string | null;
}

interface ForecastAtPointResult extends ForecastLocationContext {
  days: ForecastDaySummary[];
  hours: ForecastHourSummary[];
  loading: boolean;
  error: boolean;
}

export function useForecastAtPoint(lat: number, lng: number, enabled = true): ForecastAtPointResult {
  const [days, setDays] = useState<ForecastDaySummary[]>([]);
  const [hours, setHours] = useState<ForecastHourSummary[]>([]);
  const [location, setLocation] = useState<ForecastLocationContext>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const daysRef = useRef(days);
  daysRef.current = days;

  useEffect(() => {
    if (!enabled) return;
    const apiRoot = getApiRoot();
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
    if (!apiRoot || !token) return;

    let cancelled = false;
    setLoading(true);
    setError(false);

    void fetch(`${apiRoot}/weather/forecast/at?lat=${lat}&lng=${lng}`, {
      headers: getAuthBearerHeaders(),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setDays((data.days ?? []) as ForecastDaySummary[]);
        setHours((data.hours ?? []) as ForecastHourSummary[]);
        setLocation({
          cell_id: data.cell_id,
          district_code: data.district_code,
          service_area_code: data.service_area_code,
          district_name: data.district_name,
          region_name: data.region_name,
          country_name: data.country_name,
          postcode: data.postcode,
        });
      })
      .catch(() => {
        if (!cancelled) {
          if (daysRef.current.length === 0) setDays([]);
          setHours([]);
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [lat, lng, enabled]);

  return { days, hours, loading, error, ...location };
}
